import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Heart, Check, Lightbulb, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const FirstDatePlaybook = () => {
  const [data, setData] = useState(null);
  const [done, setDone] = useState(new Set());
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api.get("/playbooks/first-date").then((r) => {
      setData(r.data.playbook);
      setDone(new Set(r.data.progress || []));
      setActiveId(r.data.playbook.steps[0].id);
    });
  }, []);

  const toggle = async (stepId) => {
    const nowCompleted = !done.has(stepId);
    const newSet = new Set(done);
    if (nowCompleted) newSet.add(stepId); else newSet.delete(stepId);
    setDone(newSet);
    try {
      await api.post("/playbooks/first-date/progress", { step_id: stepId, completed: nowCompleted });
      if (nowCompleted) toast.success("Step marked done.");
    } catch { toast.error("Could not save progress"); }
  };

  if (!data) return <div className="p-10 text-neutral-500" data-testid="playbook-loading">Loading…</div>;

  const totalSteps = data.steps.length;
  const doneCount = data.steps.filter(s => done.has(s.id)).length;
  const pct = Math.round((doneCount / totalSteps) * 100);

  const active = data.steps.find((s) => s.id === activeId) || data.steps[0];

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start gap-4 mb-4">
        <Heart className="w-7 h-7 text-amber-500 shrink-0 mt-1" />
        <div className="flex-1">
          <div className="overline mb-1">THE PLAYBOOK</div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight" data-testid="playbook-title">
            {data.title}
          </h1>
          <p className="text-neutral-400 mt-3 max-w-2xl italic">{data.subtitle}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-8">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="overline text-neutral-500">Progress</span>
          <span className="text-amber-400" data-testid="playbook-progress">{doneCount} / {totalSteps} steps</span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-amber-500"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8 mt-10">
        {/* Steps nav */}
        <aside className="md:col-span-4 space-y-2">
          {data.steps.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              data-testid={`step-nav-${s.id}`}
              className={`w-full text-left card-surface p-5 hover-lift ${
                activeId === s.id ? "border-amber-500/50" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="overline text-neutral-500">{s.overline} · STEP 0{i+1}</div>
                {done.has(s.id) && <Check className="w-4 h-4 text-amber-400" />}
              </div>
              <div className="font-serif text-xl mt-2">{s.label}</div>
              <div className="text-sm text-neutral-400 mt-2 leading-snug">{s.summary}</div>
            </button>
          ))}
        </aside>

        {/* Active step detail */}
        <section className="md:col-span-8 card-surface p-8 md:p-10" data-testid={`step-detail-${active.id}`}>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="overline text-amber-500 mb-2">{active.overline}</div>
              <h2 className="font-serif text-3xl md:text-4xl">{active.label}</h2>
              <p className="text-neutral-400 mt-3 italic">{active.summary}</p>
            </div>
            <button
              onClick={() => toggle(active.id)}
              data-testid={`toggle-${active.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border transition-colors ${
                done.has(active.id)
                  ? "bg-amber-500/10 border-amber-500/40 text-amber-400"
                  : "btn-amber"
              }`}
            >
              {done.has(active.id) ? <><Check className="w-4 h-4" /> Done</> : "Mark done"}
            </button>
          </div>

          <div className="mt-6 space-y-3">
            {active.body?.map((line, i) => (
              <p key={i} className="text-neutral-200 leading-relaxed">{line}</p>
            ))}
          </div>

          {active.questions_bank && (
            <div className="mt-8 border-t border-white/5 pt-6">
              <div className="overline text-amber-500 mb-3 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> QUESTIONS BANK</div>
              <ol className="space-y-2 list-decimal pl-5">
                {active.questions_bank.map((q, i) => (
                  <li key={i} className="text-neutral-200">{q}</li>
                ))}
              </ol>
            </div>
          )}

          {active.checklist && (
            <div className="mt-8 border-t border-white/5 pt-6">
              <div className="overline text-amber-500 mb-3">CHECKLIST</div>
              <ul className="space-y-2">
                {active.checklist.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-neutral-200">
                    <Check className="w-4 h-4 text-amber-500 mt-1 shrink-0" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {active.prompt_phrase && (
            <div className="mt-8 border-t border-white/5 pt-6">
              <div className="overline text-amber-500 mb-3 inline-flex items-center gap-2"><Lightbulb className="w-3.5 h-3.5" /> MID-DATE PROMPT</div>
              <div className="card-elevated p-6 border-l-2 border-amber-500">
                <p className="font-serif text-xl md:text-2xl leading-snug">"{active.prompt_phrase}"</p>
              </div>
              <p className="text-xs text-neutral-500 mt-2">Open this quietly on your phone mid-date. One breath. Then look up.</p>
            </div>
          )}

          {/* Next step */}
          <div className="mt-10 flex items-center justify-between">
            <div className="text-xs text-neutral-500">
              Step {data.steps.findIndex(s => s.id === active.id) + 1} of {totalSteps}
            </div>
            {data.steps.findIndex(s => s.id === active.id) < totalSteps - 1 && (
              <button
                onClick={() => setActiveId(data.steps[data.steps.findIndex(s => s.id === active.id) + 1].id)}
                className="btn-ghost text-sm inline-flex items-center gap-2"
                data-testid="next-step-btn"
              >
                Next step <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default FirstDatePlaybook;
